/** @jsx jsx */
import { jsx } from '@emotion/core';
import { FC } from 'react';

import { makeStyles, TextField } from '@material-ui/core';

const useStyles = makeStyles(() => ({
  resize: {
    fontSize: '1.1em',
    '&::placeholder': {
      color: 'black',
    },
  },
  // (#854 F) multiline (textarea) を「サイズ変更可能なテキストボックス」にする。
  // ブラウザ既定で textarea は両方向リサイズ可能だが、横方向に伸びるとレイアウトを
  // 壊しやすいため縦方向のみに制限する。
  multilineResize: {
    fontSize: '1.1em',
    resize: 'vertical',
    '&::placeholder': {
      color: 'black',
    },
  },
}));

interface GeneralPrimaryInputProps {
  /** フォームの値 */
  value: any;
  /** フォームの値を変更した時のイベントハンドラ */
  onChange: (event: any) => void;
  /** プレイスホルダ */
  placeholder?: string;
  /** テスト用 key_id */
  dataTest?: string;
  /** (#854 F) 複数行入力 (textarea) として描画する。サイズはユーザーが縦方向にドラッグで変更可能 */
  multiline?: boolean;
  /** (#854 F) multiline 時の初期表示行数 (既定 3) */
  rows?: number;
  /** (#854 F) 値が未入力 (空文字 / undefined / null) のときに薄ピンクの背景を表示し、入力漏れを視覚的に促す */
  highlightEmpty?: boolean;
}

// 未入力ハイライト色 (#854 F: 「薄いピンク」の指示)。
// 入力済セルとの明度差は維持しつつ、本文の可読性を損なわない控えめなトーン。
const EMPTY_HIGHLIGHT_BG = '#ffe4ec';

const GeneralPrimaryInput: FC<GeneralPrimaryInputProps> = ({
  value,
  onChange,
  placeholder,
  dataTest,
  multiline = false,
  rows,
  highlightEmpty = false,
}) => {
  const classes = useStyles();
  const isEmpty = value === undefined || value === null || value === '';
  const showHighlight = highlightEmpty && isEmpty;

  return (
    <TextField
      fullWidth
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      variant="outlined"
      data-test={dataTest}
      multiline={multiline}
      // multiline 時のみ rows を設定する。未指定なら既定 3 行。
      // rows を渡すと TextareaAutosize ではなく固定行数の <textarea> となり、
      // CSS `resize: vertical` でドラッグによる手動リサイズが機能する。
      rows={multiline ? rows ?? 3 : undefined}
      InputProps={{
        classes: {
          input: multiline ? classes.multilineResize : classes.resize,
        },
        // 空欄ハイライトは OutlinedInput ルートの背景に適用 (枠線内部全体が薄ピンクになる)
        style: showHighlight ? { backgroundColor: EMPTY_HIGHLIGHT_BG } : undefined,
      }}
    />
  );
};

export default GeneralPrimaryInput;
